import { init } from '@sentry/browser'
import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'
import persist from '@alpinejs/persist'
import aa from 'search-insights'

import persistedStorePlugin from './persistedStore'
import './components/cart'
import './components/gtm'
import './components/checkout/cartRecommendations'
import helpers from './components/helpers'

// A page that throws in a loop raises hundreds of events at once, more than
// the SDK's send queue holds (30). The first few errors on a page are the
// useful ones, so stop sending after that.
const MAX_SENTRY_EVENTS_PER_PAGE = 10

// Let's configure Sentry as soon as we can
function initSentry() {
    const cookies = JSON.parse(localStorage.getItem(getFernKey('cookies')))
    const analyticsConsent = cookies?.options?.find((opt) => opt.name === 'analytics')?.value === true
    if (!analyticsConsent || !process.env.MIX_SENTRY_FRONTEND_DSN) {
        return
    }

    let sentEvents = 0

    init({
        environment: process.env.MIX_SENTRY_ENVIRONMENT,
        dsn: process.env.MIX_SENTRY_FRONTEND_DSN,
        tracesSampleRate: 0.1,
        allowUrls: [
            /https:\/\/www\.accu\.co\.uk/,
            /https:\/\/accu-components\.com/,
        ],
        ignoreErrors: [
            // Promises rejected with something other than an Error carry no
            // stack, so there's nothing to trace them back to
            'Non-Error promise rejection captured',
            // Alpine rejects this when an x-show transition is interrupted
            'Object captured as promise rejection with keys: isFromCancelledTransition',
        ],
        beforeSend(event) {
            if (event.contexts?.device?.family === 'Spider') {
                return null
            }

            // Filter out Load Failed errors due to adblockers prevent clickcease from working
            const clickceaseErrors = event.breadcrumbs?.some((crumb) => crumb.level === 'error' && crumb.data?.url?.includes('clickcease'))
            if (event.message === 'TypeError: Load failed' && clickceaseErrors) {
                return null
            }

            if (sentEvents >= MAX_SENTRY_EVENTS_PER_PAGE) {
                return null
            }
            sentEvents += 1

            return event
        }
    })
}

try {
    initSentry()
} catch (error) {
    console.error('Error initializing Sentry:', error)
}

// Load AlpineJS Plugins
Alpine.plugin([persist, persistedStorePlugin, intersect])
window.Alpine = Alpine

window.aa = aa

// Page bundles use window.helpers (and window.api, set in ./api) rather than
// bundling their own copies. This file loads before them.
window.helpers = helpers

require('./components/gclid')
require('./api')
require('./store')
require('./components/accuEligibleCheck')
require('./components/logout')
require('./partials/scrollable') // TODO: fix scrollable for cart recommendations
require('./components/scopedStore')
require('./components/intercom')
require('./components/searchInsights')

// Common Functions
const windowUrl = new URLSearchParams(window.location.search)
window.overrideCurrencyId = windowUrl.has('c') ? windowUrl.get('c') : null

window.smoothScrollToTop = () => {
    window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
    })
}

// What the email verification link did (FrontController::emailVerificationToast()), from partials/meta.tpl or the
// CAD list request (product/model.js). Every page has it for a few minutes after the click, in every tab, so it's
// shown once per verification, by its id.
window.showEmailVerificationToast = (toast) => {
    let shown = window.emailVerificationToastShown === toast.id

    try {
        const key = window.getFernKey('email_verification_toast')
        shown = shown || localStorage.getItem(key) === toast.id
        localStorage.setItem(key, toast.id)
    } catch (error) {
        // Without storage, once per page
    }

    window.emailVerificationToastShown = toast.id

    if (!shown) {
        window.dispatchEvent(new CustomEvent('create-toast', { detail: toast }))
    }
}
